import { useEffect, useState } from 'react';
import { NavLink, Routes, Route } from 'react-router-dom';
import {
  collection, doc, getDoc, onSnapshot, orderBy, query, where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { signOut } from 'firebase/auth';
import { auth, db, functions } from '../lib/firebase.js';
import { useAuth } from '../lib/auth.jsx';
import { registerForPush, listenForeground, unregisterPush } from '../lib/messaging.js';
import Availability from './responder/Availability.jsx';
import WeeklyConfirmPopup from './responder/WeeklyConfirmPopup.jsx';
import CoverRequests from './responder/CoverRequests.jsx';
import CollaborationModal from '../components/CollaborationModal.jsx';
import ResponderAIChat from '../components/ResponderAIChat.jsx';
import TicketChat, { useThreadUnread } from '../components/TicketChat.jsx';
import { useTier } from '../lib/useTier.js';

// Responder PWA. Three sections:
//   1. Permission gate - request notification permission + register FCM token
//   2. Active alerts - tickets currently RINGING_LX where this user is in the chain
//   3. My assigned - tickets where assignedTo.uid == me OR helpers contains me

export default function ResponderApp() {
  const { user, claims } = useAuth();
  const vendorId = claims?.vendor_id;

  const [vendorName, setVendorName] = useState('');
  // Task #103 — vendor-level AI add-on flag drives whether the "Ask the AI"
  // button is rendered on AssignedCards. Same source of truth as #106 customer
  // triage gating: vendor.aiAddon.enabled.
  const [aiAddonEnabled, setAiAddonEnabled] = useState(false);
  const [pushState, setPushState] = useState('idle');
  const [tickets, setTickets] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [openCoverCount, setOpenCoverCount] = useState(0);

  useEffect(() => {
    if (!vendorId) return;
    (async () => {
      try {
        const v = await getDoc(doc(db, 'vendors', vendorId));
        if (v.exists()) {
          setVendorName(v.data().name || '');
          setAiAddonEnabled(v.data().aiAddon?.enabled === true);
        }
      } catch {}
    })();
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId || !user?.uid) return;
    const q = query(
      collection(db, 'vendors', vendorId, 'coverRequests'),
      where('status', '==', 'open'),
      where('candidates', 'array-contains', user.uid),
    );
    const unsub = onSnapshot(q, (snap) => setOpenCoverCount(snap.size), () => {});
    return () => unsub();
  }, [vendorId, user?.uid]);

  useEffect(() => {
    if (!vendorId || !user?.uid) return;
    const ringingQuery = query(
      collection(db, 'vendors', vendorId, 'tickets'),
      where('status', 'in', ['RINGING_L1', 'RINGING_L2', 'RINGING_L3', 'RINGING_L4', 'RINGING_L5']),
    );
    const assignedQuery = query(
      collection(db, 'vendors', vendorId, 'tickets'),
      where('assignedTo.uid', '==', user.uid),
      orderBy('updatedAt', 'desc'),
    );
    // COLLAB 2026-05-17: tickets where I am a helper.
    const helperQuery = query(
      collection(db, 'vendors', vendorId, 'tickets'),
      where('helpers', 'array-contains', user.uid),
    );

    let r = [], a = [], h = [];
    const update = () => setTickets(dedupe([...r, ...a, ...h]));

    const unsubR = onSnapshot(ringingQuery, (snap) => {
      r = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .filter((t) => isUserInLevel(t, user.uid, currentLevelOf(t.status)));
      update();
    }, () => {});

    const unsubA = onSnapshot(assignedQuery, (snap) => {
      a = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      update();
    }, () => {});

    const unsubH = onSnapshot(helperQuery, (snap) => {
      h = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      update();
    }, () => {});

    return () => { unsubR(); unsubA(); unsubH(); };
  }, [vendorId, user?.uid]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setPushState('requesting');
      const token = await registerForPush();
      if (!mounted) return;
      if (token) {
        setPushState('registered');
        listenForeground(() => {});
      } else {
        setPushState(Notification.permission === 'denied' ? 'denied' : 'unsupported');
      }
    })();
    return () => { mounted = false; unregisterPush(); };
  }, []);

  const ringingCount = tickets.filter((t) => t.status?.startsWith('RINGING_')).length;

  useEffect(() => {
    if (ringingCount === 0 || !soundEnabled) return;

    let cancelled = false;
    let timerId;
    let audioCtx = null;

    const playRing = () => {
      if (cancelled) return;
      try {
        if (!audioCtx) {
          audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        }
        if (audioCtx.state === 'suspended') audioCtx.resume();
        const now = audioCtx.currentTime;
        [0, 0.6].forEach((delay) => {
          const osc = audioCtx.createOscillator();
          const gain = audioCtx.createGain();
          osc.type = 'sine';
          osc.frequency.value = 880;
          const t0 = now + delay;
          gain.gain.setValueAtTime(0, t0);
          gain.gain.linearRampToValueAtTime(0.45, t0 + 0.05);
          gain.gain.linearRampToValueAtTime(0, t0 + 0.45);
          osc.connect(gain).connect(audioCtx.destination);
          osc.start(t0);
          osc.stop(t0 + 0.5);
        });
      } catch (e) {
        console.warn('[sound] play failed', e);
      }
      timerId = setTimeout(playRing, 3000);
    };

    playRing();

    return () => {
      cancelled = true;
      clearTimeout(timerId);
      try { audioCtx?.close(); } catch {}
    };
  }, [ringingCount, soundEnabled]);

  function enableSound() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 660;
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.05);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.25);
      setTimeout(() => ctx.close().catch(() => {}), 500);
    } catch (e) {
      console.warn('[sound] enable failed', e);
    }
    setSoundEnabled(true);
  }

  const ringing = tickets.filter((t) => t.status?.startsWith('RINGING_'));
  const assigned = tickets.filter((t) => t.status === 'ASSIGNED');

  return (
    <div className="responder">
      <header className="requester-header">
        <div className="brand-icon-row">
          <img
            src="/giraops-logo.png"
            alt="Giraops"
            className="brand-icon-img"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div>
            <div className="brand-vendor">{vendorName || 'Giraops'}</div>
            <small style={{ color: 'var(--muted)' }}>Responder</small>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!soundEnabled && (
            <button onClick={enableSound} className="btn-primary" title="Browsers require a click to play audio">
              Enable sound
            </button>
          )}
          {soundEnabled && (
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>sound on</span>
          )}
          <button onClick={() => signOut(auth)} className="btn-ghost">Sign out</button>
        </div>
      </header>

      <nav className="responder-tabs">
        <NavLink to="/responder" end className="responder-tab">
          Alerts {ringingCount > 0 && <span className="responder-tab-badge">{ringingCount}</span>}
        </NavLink>
        <NavLink to="/responder/availability" className="responder-tab">
          My availability
        </NavLink>
        <NavLink to="/responder/cover-requests" className="responder-tab">
          Cover requests {openCoverCount > 0 && (
            <span className="responder-tab-badge responder-tab-badge-soft">{openCoverCount}</span>
          )}
        </NavLink>
        {aiAddonEnabled && (
          <NavLink to="/responder/ai-help" className="responder-tab">
            🔎 Ask the AI
          </NavLink>
        )}
      </nav>

      <main className="requester-main">
        <Routes>
          <Route
            path="/"
            element={
              <AlertsView
                pushState={pushState}
                ringing={ringing}
                assigned={assigned}
                aiAddonEnabled={aiAddonEnabled}
              />
            }
          />
          <Route path="availability" element={<Availability />} />
          <Route path="cover-requests" element={<CoverRequests />} />
          <Route
            path="ai-help"
            element={
              aiAddonEnabled
                ? <AskAIView />
                : <div className="placeholder">AI add-on isn't enabled for your vendor.</div>
            }
          />
        </Routes>
      </main>

      <WeeklyConfirmPopup />

    </div>
  );
}

function AlertsView({ pushState, ringing, assigned, aiAddonEnabled }) {
  return (
    <>
      <PushBanner state={pushState} retry={() => location.reload()} />

      <section style={{ marginTop: 16 }}>
        <h2 style={{ fontSize: 18 }}>Incoming alerts ({ringing.length})</h2>
        {ringing.length === 0 ? (
          <div className="placeholder">No active alerts. You'll be notified when a ticket needs you.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {ringing.map((t) => <RingingCard key={t.id} t={t} />)}
          </div>
        )}
      </section>

      <section style={{ marginTop: 32 }}>
        <h2 style={{ fontSize: 18 }}>My assigned tickets ({assigned.length})</h2>
        {assigned.length === 0 ? (
          <div className="placeholder">Nothing assigned to you right now.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {assigned.map((t) => <AssignedCard key={t.id} t={t} aiAddonEnabled={aiAddonEnabled} />)}
          </div>
        )}
      </section>
    </>
  );
}

// Standalone Ask-the-AI tab — works without an active ticket. Lets responders
// query the vendor's knowledge base (uploaded manuals) for fix guidance anytime.
function AskAIView() {
  return (
    <section style={{ marginTop: 16 }}>
      <h2 style={{ fontSize: 18, marginBottom: 4 }}>Ask the AI</h2>
      <p style={{ color: 'var(--muted)', marginTop: 0, marginBottom: 12, fontSize: 13 }}>
        Get on-the-spot help grounded in the manuals your administrator uploaded. No active ticket
        required — ask anything about your vendor's equipment (fault codes, procedures, torque specs…).
      </p>
      <ResponderAIChat embedded />
    </section>
  );
}

function PushBanner({ state, retry }) {
  if (state === 'registered') return null;
  if (state === 'requesting') return <div className="placeholder">Setting up alerts...</div>;
  if (state === 'denied') return (
    <div className="error">
      Notifications are blocked. Open browser settings, allow notifications for this site, then <a href="#" onClick={retry}>reload</a>.
    </div>
  );
  if (state === 'unsupported') return (
    <div className="error">
      This browser doesn't support push notifications. Use Chrome on Android (or iOS 16.4+ with the app "Added to home screen") for reliable alerts.
    </div>
  );
  return null;
}

function RingingCard({ t }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const level = currentLevelOf(t.status);

  async function accept() {
    setBusy(true); setErr(null);
    try {
      await httpsCallable(functions, 'acceptTicket')({ ticketId: t.id });
    } catch (e) {
      setErr(prettyError(e));
    } finally {
      setBusy(false);
    }
  }
  async function reject() {
    if (!confirm('Skip this call? It will be reported to the manager.')) return;
    setBusy(true); setErr(null);
    try {
      await httpsCallable(functions, 'rejectTicket')({ ticketId: t.id });
    } catch (e) {
      setErr(prettyError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card alert-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong style={{ color: '#dc2626' }}>RINGING - L{level}</strong>
        <small style={{ color: 'var(--muted)' }}>{t.skillName}</small>
      </div>
      <div style={{ marginTop: 8 }}>
        <strong>{t.customerName}</strong>
        {t.siteName && <span style={{ color: 'var(--muted)' }}> - {t.siteName}</span>}
      </div>
      {/* Walk-up QR tickets carry a free-text address — the tech needs it to
          actually get there, so it never hides behind a click. */}
      {t.address && (
        <a
          href={`https://maps.google.com/?q=${encodeURIComponent(t.address)}`}
          target="_blank"
          rel="noreferrer"
          style={{ display: 'block', marginTop: 4, fontSize: 14, color: '#0369a1' }}
        >
          📍 {t.address}
        </a>
      )}
      <div style={{ marginTop: 6, fontSize: 14 }}>{t.description}</div>
      {(t.photoUrls || []).length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {t.photoUrls.map((url, i) => (
            <a key={i} href={url} target="_blank" rel="noreferrer">
              <img src={url} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6 }} />
            </a>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button onClick={accept} disabled={busy} className="btn-primary" style={{ flex: 1, background: '#10b981' }}>
          {busy ? 'Accepting...' : 'Accept'}
        </button>
        <button onClick={reject} disabled={busy} className="btn-ghost-danger" style={{ flex: 1 }}>
          Skip / reject
        </button>
      </div>
      {err && <div className="error">{err}</div>}
    </div>
  );
}

function AssignedCard({ t, aiAddonEnabled }) {
  const { user, claims } = useAuth();
  const { tier } = useTier();
  const [collabMode, setCollabMode] = useState(null);
  const [closeOpen, setCloseOpen] = useState(false); // FEEDBACK 2026-05-17
  // Task #103 — AI assistant modal toggle.
  const [aiChatOpen, setAiChatOpen] = useState(false);
  // CHAT (spec-ticket-chat.md) — in-app conversation replaced the WhatsApp handoff.
  const [chatOpen, setChatOpen] = useState(false);
  const vendorId = claims?.vendor_id;
  const chatUnread = useThreadUnread(vendorId, t, user?.uid);

  const phone = t.customerPhone;
  const isAssignee = t.assignedTo?.uid === user?.uid;
  const isHelper = Array.isArray(t.helpers) && t.helpers.includes(user?.uid);
  const collabUnlocked = !!tier?.features?.ticketCollaboration;
  const myName = (isAssignee && t.assignedTo?.name)
    || user?.displayName || user?.email || 'Technician';

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong style={{ color: '#10b981' }}>
          {isAssignee ? 'ASSIGNED' : isHelper ? 'HELPING' : 'ASSIGNED'}
        </strong>
        <small style={{ color: 'var(--muted)' }}>{t.skillName}</small>
      </div>
      <div style={{ marginTop: 8 }}>
        <strong>{t.customerName}</strong>
        {t.siteName && <span style={{ color: 'var(--muted)' }}> - {t.siteName}</span>}
      </div>
      {/* Walk-up QR tickets carry a free-text address — the tech needs it to
          actually get there, so it never hides behind a click. */}
      {t.address && (
        <a
          href={`https://maps.google.com/?q=${encodeURIComponent(t.address)}`}
          target="_blank"
          rel="noreferrer"
          style={{ display: 'block', marginTop: 4, fontSize: 14, color: '#0369a1' }}
        >
          📍 {t.address}
        </a>
      )}
      {!isAssignee && t.assignedTo?.name && (
        <div style={{ marginTop: 4, fontSize: 13, color: 'var(--muted)' }}>
          Lead: {t.assignedTo.name}
        </div>
      )}
      <div style={{ marginTop: 6, fontSize: 14 }}>{t.description}</div>

      {/* CHAT — the conversation lives in the app now (full record on the ticket).
          A plain phone link stays for actual voice calls. */}
      <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          className="btn-primary"
          style={{ flex: 1, position: 'relative' }}
          onClick={() => setChatOpen(true)}
        >
          💬 Chat with {t.customerName?.split(' ')[0] || 'requester'}
          {chatUnread && (
            <span style={{
              position: 'absolute', top: 6, right: 8, width: 10, height: 10,
              borderRadius: '50%', background: '#dc2626',
            }} />
          )}
        </button>
        {phone && (
          <a href={`tel:${phone}`} className="btn-ghost" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}>
            📞 Call
          </a>
        )}
      </div>
      {chatOpen && (
        <TicketChat
          vendorId={vendorId}
          ticket={t}
          me={{ uid: user.uid, name: myName, role: 'responder' }}
          onClose={() => setChatOpen(false)}
        />
      )}

      {isAssignee && (
        <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
          <button
            className="btn-ghost"
            style={{ flex: 1 }}
            onClick={() => collabUnlocked && setCollabMode('transfer')}
            disabled={!collabUnlocked}
            title={collabUnlocked ? 'Hand this ticket off' : 'Enterprise feature - upgrade to enable'}
          >
            Transfer{collabUnlocked ? '' : ' (Enterprise)'}
          </button>
          <button
            className="btn-ghost"
            style={{ flex: 1 }}
            onClick={() => collabUnlocked && setCollabMode('helper')}
            disabled={!collabUnlocked}
            title={collabUnlocked ? 'Loop in another responder' : 'Enterprise feature - upgrade to enable'}
          >
            Add helper{collabUnlocked ? '' : ' (Enterprise)'}
          </button>
        </div>
      )}

      {/* Task #103 — AI assistant. Visible to whoever's on the card
          (assignee or helper) when the vendor has the AI add-on on. */}
      {aiAddonEnabled && (isAssignee || isHelper) && (
        <div style={{ marginTop: 8 }}>
          <button
            className="btn-ghost"
            style={{ width: '100%', borderColor: '#5b21b6', color: '#5b21b6' }}
            onClick={() => setAiChatOpen(true)}
          >
            🔎 Ask the AI
          </button>
        </div>
      )}
      {aiChatOpen && (
        <ResponderAIChat
          ticket={t}
          onClose={() => setAiChatOpen(false)}
        />
      )}

      {isAssignee && (
        <div style={{ marginTop: 8 }}>
          <button
            className="btn-primary"
            style={{ width: '100%', background: '#10b981' }}
            onClick={() => setCloseOpen(true)}
          >
            Close ticket + log note
          </button>
        </div>
      )}

      {collabMode && (
        <CollaborationModal
          mode={collabMode}
          ticket={t}
          onClose={() => setCollabMode(null)}
          onDone={() => { /* listener picks up the change */ }}
        />
      )}

      {closeOpen && (
        <CloseTicketModal
          ticket={t}
          onClose={() => setCloseOpen(false)}
        />
      )}
    </div>
  );
}

// ============================================================================
// CloseTicketModal - FEEDBACK 2026-05-17
// Optional internal note for the admin + optional product feedback to Giraops.
// The customer feedback prompt is shown to the customer in their app after close.
// ============================================================================

function CloseTicketModal({ ticket, onClose }) {
  const [note, setNote] = useState('');
  const [productFeedback, setProductFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit() {
    setErr(null);
    setBusy(true);
    try {
      await httpsCallable(functions, 'closeTicket')({
        ticketId: ticket.id,
        responderNote: note.trim() || undefined,
      });
      // Best-effort product feedback after close — failure doesn't block the close.
      if (productFeedback.trim()) {
        try {
          await httpsCallable(functions, 'submitProductFeedback')({
            ticketId: ticket.id,
            kind: 'responder',
            comment: productFeedback.trim(),
          });
        } catch { /* ignore */ }
      }
      onClose();
    } catch (e) {
      setErr(e?.message || 'Could not close the ticket.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ra-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ra-modal" style={{ maxWidth: 480 }}>
        <h3>Close ticket</h3>
        <p className="settings-help" style={{ marginTop: 0 }}>
          Closing marks this resolved and prompts <strong>{ticket.customerName}</strong> to
          rate the service in their app. Your note below goes to your administrator -
          the customer does not see it.
        </p>

        <div className="ra-field">
          <label htmlFor="close-note">
            Internal note (optional) - goes to admin
          </label>
          <textarea
            id="close-note"
            rows={3}
            maxLength={1000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="ra-input"
            placeholder="What did you find / fix? Anything ops should know?"
            disabled={busy}
          />
        </div>

        <div className="ra-field" style={{
          marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb',
        }}>
          <label htmlFor="close-product">
            Optional: feedback about Giraops itself
          </label>
          <p className="settings-help" style={{ marginTop: 0, fontSize: 12 }}>
            Anything about the app that helped or got in the way? Goes to the
            Giraops team, not your admin.
          </p>
          <textarea
            id="close-product"
            rows={2}
            maxLength={1000}
            value={productFeedback}
            onChange={(e) => setProductFeedback(e.target.value)}
            className="ra-input"
            placeholder="(Optional)"
            disabled={busy}
          />
        </div>

        {err && <div className="error-banner" style={{ marginTop: 8 }}>{err}</div>}

        <div className="ra-modal-actions" style={{ marginTop: 14 }}>
          <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={busy} style={{ background: '#10b981' }}>
            {busy ? 'Closing...' : 'Close ticket'}
          </button>
        </div>
      </div>
    </div>
  );
}

function currentLevelOf(status) {
  const m = /^RINGING_L(\d+)$/.exec(status || '');
  return m ? parseInt(m[1], 10) : null;
}
function isUserInLevel(t, uid, level) {
  if (!level || !Array.isArray(t.chainSnapshot)) return false;
  const lvl = t.chainSnapshot[level - 1];
  return lvl && Array.isArray(lvl.responderUids) && lvl.responderUids.includes(uid);
}
function dedupe(arr) {
  const seen = new Set();
  return arr.filter((x) => (seen.has(x.id) ? false : (seen.add(x.id), true)));
}
function prettyError(e) {
  const m = e?.message || '';
  if (m.includes('failed-precondition')) return 'Already taken or no longer ringing.';
  return m || 'Action failed.';
}
